import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import apiService from '../../services/api';
import InfrastructureSectionPanel from './InfrastructureSectionPanel';

// ─── Known non-hour column keys ─────────────────────────────────────────────

const PLATFORM_META_COLS = new Set([
    'organization_id', 'organization_code', 'organization_name',
    'app_version', 'build_number', 'total',
]);

const ENTERPRISE_META_COLS = new Set(['app_version', 'build_number', 'total']);



// ─── Helpers ─────────────────────────────────────────────────────────────────

const getHourCols = (rows, metaSet) =>
    rows && rows.length > 0
        ? Object.keys(rows[0]).filter(k => !metaSet.has(k))
        : [];

const getPeak = (row, hourCols) =>
    Math.max(0, ...hourCols.map(h => parseInt(row[h]) || 0));

const cellVariant = (value, peak) => {
    if (!value || peak === 0) return '';
    const ratio = value / peak;
    if (ratio === 1) return 'table-danger';
    if (ratio >= 0.7) return 'table-warning';
    if (ratio >= 0.4) return 'bg-warning bg-opacity-10';
    return '';
};

// ─── Reusable hourly table ────────────────────────────────────────────────────

const HourlyTable = ({ rows, identityCols, identityLabels, excludeCols = [] }) => {
    const hourCols = getHourCols(rows, new Set([...identityCols, 'total', ...excludeCols]));

    const totals = Object.fromEntries(
        hourCols.map(h => [h, rows.reduce((s, r) => s + (parseInt(r[h]) || 0), 0)])
    );
    const grandTotal = rows.reduce((s, r) => s + (parseInt(r.total) || 0), 0);

    return (
        <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.82rem' }}>
                <thead className="table-secondary">
                    <tr>
                        {identityLabels.map((label, i) => (
                            <th key={i} style={{ minWidth: i === 0 ? '120px' : '90px', whiteSpace: 'nowrap' }}>
                                {label}
                            </th>
                        ))}
                        {hourCols.map(h => (
                            <th key={h} className="text-center" style={{ minWidth: '46px' }}>{h}</th>
                        ))}
                        <th className="text-center bg-info bg-opacity-10" style={{ minWidth: '58px' }}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, idx) => {
                        const peak = getPeak(row, hourCols);
                        return (
                            <tr key={idx}>
                                {identityCols.map((col, i) => (
                                    <td
                                        key={i}
                                        className={i === 0 ? 'fw-medium' : 'text-muted small'}
                                        style={i === 0 ? { position: 'sticky', left: 0, background: 'white', zIndex: 1 } : {}}
                                    >
                                        {row[col] ?? '—'}
                                    </td>
                                ))}
                                {hourCols.map(h => {
                                    const val = parseInt(row[h]) || 0;
                                    return (
                                        <td key={h} className={`text-center ${cellVariant(val, peak)}`}>
                                            {val || ''}
                                        </td>
                                    );
                                })}
                                <td className="text-center fw-semibold bg-info bg-opacity-10">
                                    {row.total ?? 0}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot className="table-dark">
                    <tr>
                        {identityCols.map((_, i) => (
                            <td key={i} className="fw-bold">{i === 0 ? 'Total' : ''}</td>
                        ))}
                        {hourCols.map(h => (
                            <td key={h} className="text-center fw-bold">{totals[h] || ''}</td>
                        ))}
                        <td className="text-center fw-bold">{grandTotal}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};



// ─── Empty state ──────────────────────────────────────────────────────────────

const EmptyState = ({ message }) => (
    <div className="text-center text-muted py-4">
        <i className="bi bi-inbox display-6 d-block mb-2"></i>
        {message}
    </div>
);

// ─── Section 1 – Platform Logins ─────────────────────────────────────────────

const PlatformLoginsSection = () => {
    const [organizations, setOrganizations] = useState([]);
    const [orgsLoading, setOrgsLoading] = useState(false);
    const [orgsError, setOrgsError] = useState('');

    const [selectedOrg, setSelectedOrg] = useState('all');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Load org list once
    useEffect(() => {
        const loadOrgs = async () => {
            setOrgsLoading(true);
            const res = await apiService.getOrganizations(1, 1000, '');
            if (res.success) {
                setOrganizations([
                    { id: 'all', name: 'All Organizations' },
                    ...(res.data || []).map(o => ({ id: o.id, name: o.name })),
                ]);
            } else {
                setOrgsError(res.error || 'Failed to load organizations');
            }
            setOrgsLoading(false);
        };
        loadOrgs();
    }, []);

    const fetchData = useCallback(async (orgId) => {
        setLoading(true);
        setError('');
        const res = await apiService.getDailyPlatformVersionStatistics(orgId);
        if (res.success) setData(res.data);
        else setError(res.error || 'Failed to load platform login statistics');
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchData(selectedOrg);
    }, [selectedOrg, fetchData]);

    const handleOrgChange = (e) => setSelectedOrg(e.target.value);

    const isEmpty = !data || data.length === 0;
    const isAllOrg = selectedOrg === 'all';

    return (
        <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                    <i className="bi bi-phone me-2 text-primary"></i>
                    Platform Logins by App Version
                    {!isAllOrg && <Badge bg="primary" className="ms-2 fw-normal">Per Organisation</Badge>}
                </h6>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => fetchData(selectedOrg)}
                    disabled={loading}
                >
                    <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                </Button>
            </Card.Header>
            <Card.Body>
                {/* Organisation selector */}
                <Row className="mb-3">
                    <Col xs={12} md={5} lg={4}>
                        <Form.Group>
                            <Form.Label className="small fw-semibold mb-1">Organisation</Form.Label>
                            {orgsLoading ? (
                                <div className="d-flex align-items-center gap-2 py-1">
                                    <Spinner animation="border" size="sm" />
                                    <span className="small text-muted">Loading…</span>
                                </div>
                            ) : orgsError ? (
                                <Alert variant="danger" className="small py-1 mb-0">{orgsError}</Alert>
                            ) : (
                                <Form.Select
                                    size="sm"
                                    value={selectedOrg}
                                    onChange={handleOrgChange}
                                    disabled={loading}
                                >
                                    {organizations.map(o => (
                                        <option key={o.id} value={o.id}>{o.name}</option>
                                    ))}
                                </Form.Select>
                            )}
                        </Form.Group>
                    </Col>
                    {isAllOrg && (
                        <Col xs={12} md={7} lg={8} className="d-flex align-items-end">
                            <p className="small text-muted mb-0">
                                <i className="bi bi-info-circle me-1"></i>
                                Showing aggregate totals only. Select a specific organisation to see a breakdown by App Version and Build number.
                            </p>
                        </Col>
                    )}
                </Row>

                <InfrastructureSectionPanel loading={loading} error={error} onRefresh={() => fetchData(selectedOrg)}>
                    {isEmpty ? (
                        <EmptyState message="No login data available for the selected period." />
                    ) : (() => {
                        const first = data[0];
                        const COL_MAP = {
                            organization_code: 'Org. Code',
                            organization_name: 'Organization Name',
                            app_version: 'App Version',
                            build_number: 'Build',
                        };
                        const identityCols = Object.keys(COL_MAP).filter(k => k in first);
                        const identityLabels = identityCols.map(k => COL_MAP[k]);
                        return (
                            <HourlyTable
                                rows={data}
                                identityCols={identityCols}
                                identityLabels={identityLabels}
                                excludeCols={['organization_id']}
                            />
                        );
                    })()}
                </InfrastructureSectionPanel>
            </Card.Body>
        </Card>
    );
};

// ─── Section 2 – Enterprise Logins ───────────────────────────────────────────

const EnterpriseLoginsSection = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        const res = await apiService.getDailyEnterpriseVersionStatistics();
        if (res.success) setData(res.data);
        else setError(res.error || 'Failed to load enterprise login statistics');
        setLoading(false);
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const isEmpty = !data || data.length === 0;

    return (
        <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                    <i className="bi bi-building me-2 text-secondary"></i>
                    Enterprise (Back-office) Logins by App Version
                </h6>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={fetchData}
                    disabled={loading}
                >
                    <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                </Button>
            </Card.Header>
            <Card.Body>
                <InfrastructureSectionPanel loading={loading} error={error} onRefresh={fetchData}>
                    {isEmpty ? (
                        <EmptyState message="No enterprise login data available for the selected period." />
                    ) : (
                        <HourlyTable
                            rows={data}
                            identityCols={['app_version', 'build_number']}
                            identityLabels={['App Version', 'Build']}
                        />
                    )}
                </InfrastructureSectionPanel>
            </Card.Body>
        </Card>
    );
};

// ─── Main export ──────────────────────────────────────────────────────────────

const InfrastructureWebappPanel = () => (
    <>
        <PlatformLoginsSection />
        <EnterpriseLoginsSection />
    </>
);

export default InfrastructureWebappPanel;
